// DTOs de la programación semanal de técnicos de mantenimiento.
//
//   - GET    /api/mantenimiento/programacion-tecnicos?semana_iso=YYYY-Www
//   - POST   /api/mantenimiento/programacion-tecnicos
//   - DELETE /api/mantenimiento/programacion-tecnicos/:id
//   - POST   /api/mantenimiento/programacion-tecnicos/copiar-semana
//
// Clon del patrón C3 (programación de supervisores) con dos diferencias:
//   - VARIOS técnicos por (fecha, turno) — UNIQUE incluye tecnico_user_id
//   - planta_id opcional por asignación (NULL = cubre todas las plantas)
//
// La programación resuelve "el técnico de turno" para el fan-out de
// notificaciones cuando un líder registra un paro de mantenimiento.
import { z } from 'zod';
const semanaIsoRegex = /^[0-9]{4}-W[0-9]{2}$/;
const horaTimeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
export const tecnicoResumenSchema = z.object({
    /** user_id del técnico (users.id). */
    id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    email: z.string().email(),
    iniciales: z.string().min(1).max(4),
    /** Planta base de su asignación en usuarios_mantenimiento (NULL = todas). */
    planta_id: z.string().uuid().nullable(),
    planta_nombre: z.string().nullable(),
});
const turnoResumenMttoSchema = z.object({
    id: z.string().uuid(),
    numero: z.number().int().min(1).max(4),
    nombre: z.string(),
    hora_inicio: z.string().regex(horaTimeRegex),
    hora_fin: z.string().regex(horaTimeRegex),
    cruza_medianoche: z.boolean(),
});
export const programacionTecnicoEnriquecidaSchema = z.object({
    id: z.string().uuid(),
    semana_iso: z.string().regex(semanaIsoRegex),
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    /**
     * Número del turno (1-4). El grid agrupa por NÚMERO: cada planta tiene su
     * propia fila en `turnos` para el mismo turno operativo, y la programación
     * de técnicos es a nivel tenant (planta_id opcional aparte).
     */
    turno_numero: z.number().int().min(1).max(4),
    tecnico_user_id: z.string().uuid(),
    tecnico_nombre: z.string(),
    tecnico_apellido: z.string(),
    /** Scope de la asignación: NULL = todas las plantas. */
    planta_id: z.string().uuid().nullable(),
    planta_nombre: z.string().nullable(),
});
export const programacionTecnicosQuerySchema = z.object({
    semana_iso: z.string().regex(semanaIsoRegex).optional(),
    /**
     * Filtra los turnos del grid a los de UNA planta (sin filtro: turnos del
     * tenant deduplicados por número+horario). Las asignaciones no se filtran:
     * viven por número de turno con planta propia opcional.
     */
    planta_id: z.string().uuid().optional(),
});
export const programacionTecnicosSemanaSchema = z.object({
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    semana_iso: z.string().regex(semanaIsoRegex),
    /** Fechas reales de la semana operativa (7 días) en YYYY-MM-DD. */
    dias: z.array(z.string().date()).length(7),
    fecha_hoy: z.string().date(),
    /** Pool: usuarios_mantenimiento con rol_mtto='tecnico' y activo=TRUE. */
    tecnicos: z.array(tecnicoResumenSchema),
    turnos: z.array(turnoResumenMttoSchema),
    programaciones: z.array(programacionTecnicoEnriquecidaSchema),
    metricas: z.object({
        /** Celdas (fecha × turno) con al menos un técnico asignado. */
        celdas_cubiertas: z.number().int().min(0),
        celdas_total: z.number().int().min(0),
        sin_cubrir_count: z.number().int().min(0),
    }),
    /** Turnos asignados por técnico en la semana (keys = user_id). */
    turnos_por_tecnico: z.record(z.string().uuid(), z.number().int().min(0)),
    semana_anterior_tiene_data: z.boolean(),
    /** True si el actor puede crear/borrar (rol de módulo coordinador_mtto). */
    puede_editar: z.boolean(),
});
export const programacionTecnicoCreateSchema = z.object({
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    tecnico_user_id: z.string().uuid(),
    planta_id: z.string().uuid().nullable().optional(),
});
export const programacionTecnicosCopiarInputSchema = z.object({
    semana_iso_destino: z.string().regex(semanaIsoRegex),
    /** Opcional: si falta, usa la semana inmediatamente anterior a la destino. */
    semana_iso_origen: z.string().regex(semanaIsoRegex).optional(),
});
export const programacionTecnicosCopiarResponseSchema = z.object({
    filas_creadas: z.number().int().min(0),
    semana_iso_origen: z.string().regex(semanaIsoRegex),
    semana_iso_destino: z.string().regex(semanaIsoRegex),
});
//# sourceMappingURL=mantenimiento_programacion_tecnicos.js.map